import React, { useEffect, useState } from "react";
import { Library, X, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

// Link existing Knowledge Center files to content (no re-upload). Stores ids only.
export function KnowledgeFilePicker({ value = [], onChange, testPrefix = "kc-link" }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [names, setNames] = useState({});
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get("/knowledge/picker", { params: { q } }).then((r) => {
      setRows(r.data.items || []); setNames((n) => ({ ...n, ...Object.fromEntries((r.data.items || []).map((x) => [x.id, x.display_title || x.title])) }));
    }).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  useEffect(() => {
    if (!value.length) return;
    api.get("/knowledge/picker").then((r) => setNames((n) => ({ ...Object.fromEntries((r.data.items || []).map((x) => [x.id, x.display_title || x.title])), ...n }))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-1.5" data-testid={`${testPrefix}-picker`}>
      <div className="flex flex-wrap gap-1.5">
        {value.map((id) => <span key={id} className="inline-flex items-center gap-1 text-xs rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5" data-testid={`${testPrefix}-chip-${id}`}>
          <Library className="w-3 h-3" />{names[id] || "Knowledge file"}<button type="button" onClick={() => onChange(value.filter((x) => x !== id))} aria-label="Remove link"><X className="w-3 h-3" /></button></span>)}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="inline-flex items-center gap-1 text-xs font-medium text-[#006090] border border-dashed border-[#006090]/40 rounded-full px-2 py-0.5 hover:bg-[#006090]/5" data-testid={`${testPrefix}-open`}>
              <Library className="w-3 h-3" />Link from Knowledge Center</button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-2" align="start">
            <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search existing files…" className="h-8 pl-7 text-sm" data-testid={`${testPrefix}-search`} /></div>
            <div className="max-h-64 overflow-y-auto mt-1.5">
              {rows.map((r) => { const on = value.includes(r.id); return (
                <button key={r.id} type="button" onClick={() => onChange(on ? value.filter((x) => x !== r.id) : [...value, r.id])} data-testid={`${testPrefix}-option-${r.id}`}
                  className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-slate-50 ${on ? "bg-[#006090]/10" : ""}`}>
                  <span className="font-medium text-slate-800 block truncate">{r.display_title || r.title}</span>
                  <span className="text-slate-400 truncate block">{r.original_filename || r.scope_type}</span></button>); })}
              {!rows.length && <p className="text-xs text-slate-400 p-2">No matching files you can access.</p>}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export function LinkedKnowledgeFiles({ files = [] }) {
  if (!files.length) return null;
  const open = async (f) => { const { viewAttachment } = await import("@/lib/files"); viewAttachment(`/knowledge-assistant/sources/${f.id}/file`, f.title); };
  return (
    <div className="mt-6 rounded-lg border border-slate-200 p-3" data-testid="linked-kc-files">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Source documents</p>
      {files.map((f) => <button key={f.id} type="button" onClick={() => open(f)} className="flex items-center gap-1.5 text-sm text-[#006090] hover:underline" data-testid={`linked-kc-file-${f.id}`}><Library className="w-3.5 h-3.5" />{f.title}</button>)}
    </div>
  );
}
